'use client';

import { useEffect } from 'react';

import { useRouter } from 'next/navigation';

// Capacitor's addListener returns either a Promise<PluginListenerHandle>
// or, in some plugin versions, a synchronous PluginListenerHandle. We treat
// the result as "thenable-ish" and normalize via Promise.resolve().
interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

interface AppPlugin {
  addListener: (
    event: 'appUrlOpen',
    handler: (e: { url: string }) => void,
  ) => PluginListenerHandle | Promise<PluginListenerHandle>;
  getLaunchUrl?: () => Promise<{ url?: string } | null>;
}

interface CapacitorGlobal {
  Plugins?: { App?: AppPlugin };
  isNativePlatform?: () => boolean;
}

// Module-scoped so HMR / remounts don't re-route the launch URL.
// The launch URL is a single cold-start event per app session.
let launchUrlHandled = false;

/**
 * Routes Universal Link opens (WhatsApp CTAs, Safari taps, etc.) into the
 * in-app Next.js router when running inside the Capacitor native shell.
 *
 * The Capacitor webview is already loaded on the tenant domain via
 * `server.url` in capacitor.config.json, so we only need to route by
 * pathname + search — never swap the origin.
 *
 * No-op on the web; the WebView is the only place this matters.
 */
export function CapacitorDeepLinkHandler() {
  const router = useRouter();

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor as CapacitorGlobal | undefined;

    if (!cap?.isNativePlatform?.()) return;

    const app = cap.Plugins?.App;
    if (!app) return;

    const route = (raw: string) => {
      try {
        const url = new URL(raw);
        const target = `${url.pathname}${url.search}${url.hash}`;
        if (target && target !== '/') {
          router.push(target);
        }
      } catch {
        // Ignore malformed URLs rather than crash the shell.
      }
    };

    // Mutable handle so the cleanup closure sees the resolved remover even
    // when the addListener promise resolves after unmount.
    const handle: {
      cancelled: boolean;
      remove: (() => Promise<void> | void) | null;
    } = {
      cancelled: false,
      remove: null,
    };

    // Capacitor versions disagree on whether addListener returns the handle
    // directly or a Promise of it. Promise.resolve() normalizes both.
    Promise.resolve(
      app.addListener('appUrlOpen', (event) => route(event.url)),
    )
      .then((listener) => {
        if (handle.cancelled) {
          listener.remove();
        } else {
          handle.remove = listener.remove;
        }
      })
      .catch(() => {
        // addListener can fail on older Capacitor versions; safe to ignore.
      });

    // Handle cold-start: app was opened from a link while not running.
    // Only the first mount per app session should consume the launch URL.
    if (!launchUrlHandled) {
      launchUrlHandled = true;
      app.getLaunchUrl?.().then((result) => {
        if (result?.url) route(result.url);
      });
    }

    return () => {
      handle.cancelled = true;
      handle.remove?.();
    };
  }, [router]);

  return null;
}
